import React from 'react';
import { Menu, TextInput } from '@mantine/core';
import { TbBuilding, TbCalendar, TbChevronDown, TbFolder, TbPlus, TbSearch } from 'react-icons/tb';
import { Chip } from './style';

interface BreadcrumbOption {
  label: string;
  value: string;
}

interface FooterAction {
  label: string;
  onClick: () => void;
}

interface BreadcrumbChipProps {
  icon: React.ReactNode;
  label: string;
  title?: string;
  options?: BreadcrumbOption[];
  onSelect?: (value: string) => void;
  ariaLabel: string;
  footerAction?: FooterAction;
  /** Value to mark as the active item in the menu (e.g. current project id). */
  activeValue?: string;
  /** Renders a search input at the top of the dropdown. */
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (q: string) => void;
  searchPlaceholder?: string;
  /** Optional empty-state message when options.length === 0 (e.g. "No matches"). */
  emptyLabel?: string;
}

const BreadcrumbChip: React.FC<BreadcrumbChipProps> = ({
  icon,
  label,
  title,
  options,
  onSelect,
  ariaLabel,
  footerAction,
  activeValue,
  searchable,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  emptyLabel
}) => {
  const hasOptions = options && options.length > 0;
  const hasMenu = hasOptions || !!footerAction || !!searchable;

  const trigger = (
    <Chip type="button" aria-label={ariaLabel} disabled={!hasMenu && !onSelect}>
      {icon}
      <span className="label">{label}</span>
      <TbChevronDown size={14} />
    </Chip>
  );

  if (!hasMenu) {
    return trigger;
  }

  // closeOnItemClick=false so typing in the search input doesn't dismiss the menu.
  return (
    <Menu shadow="md" width={280} position="bottom-start" closeOnItemClick={false} zIndex={1100}>
      <Menu.Target>{trigger}</Menu.Target>
      <Menu.Dropdown>
        {title && <Menu.Label>{title}</Menu.Label>}
        {searchable && (
          <div style={{ padding: '4px 8px 8px' }}>
            <TextInput
              size="xs"
              placeholder={searchPlaceholder ?? 'Search…'}
              value={searchValue ?? ''}
              onChange={e => onSearchChange?.(e.currentTarget.value)}
              leftSection={<TbSearch size={14} />}
              onKeyDown={e => e.stopPropagation()}
              aria-label={searchPlaceholder ?? 'Search'}
            />
          </div>
        )}
        {hasOptions ? (
          // Cap visible items to ~12 rows (Mantine xs Menu.Item ≈ 32px). Above
          // that, the user scrolls or narrows the list via the search input.
          <div style={{ maxHeight: 384, overflowY: 'auto' }}>
            {options!.map(opt => (
              <Menu.Item
                key={opt.value}
                onClick={() => onSelect?.(opt.value)}
                fw={opt.value === activeValue ? 600 : 400}
              >
                {opt.label}
              </Menu.Item>
            ))}
          </div>
        ) : (
          searchable && <Menu.Label c="dimmed">{emptyLabel ?? 'No matches'}</Menu.Label>
        )}
        {footerAction && (
          <>
            {(hasOptions || searchable) && <Menu.Divider />}
            <Menu.Item leftSection={<TbPlus size={14} />} c="blue.6" onClick={footerAction.onClick}>
              {footerAction.label}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export interface CompanyBreadcrumbProps {
  label: string;
  options?: BreadcrumbOption[];
  onSelect?: (value: string) => void;
  activeValue?: string;
}

export const CompanyBreadcrumb: React.FC<CompanyBreadcrumbProps> = props => (
  <BreadcrumbChip
    icon={<TbBuilding size={16} />}
    ariaLabel="Select company"
    title="Companies"
    {...props}
  />
);

export interface ProjectBreadcrumbProps {
  label: string;
  options?: BreadcrumbOption[];
  onSelect?: (value: string) => void;
  footerAction?: FooterAction;
  activeValue?: string;
  searchValue?: string;
  onSearchChange?: (q: string) => void;
}

export const ProjectBreadcrumb: React.FC<ProjectBreadcrumbProps> = props => (
  <BreadcrumbChip
    icon={<TbFolder size={16} />}
    ariaLabel="Select project"
    title="Projects"
    searchable
    searchPlaceholder="Search projects…"
    emptyLabel="No projects match"
    {...props}
  />
);

export interface PeriodBreadcrumbProps {
  label: string;
  options?: BreadcrumbOption[];
  onSelect?: (value: string) => void;
  activeValue?: string;
}

export const PeriodBreadcrumb: React.FC<PeriodBreadcrumbProps> = props => (
  <BreadcrumbChip
    icon={<TbCalendar size={16} />}
    ariaLabel="Select period"
    title="Periods"
    {...props}
  />
);
